/**
 * Color utility functions for dynamic theming
 */

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex values
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return { r, g, b };
}

/**
 * Convert RGB to HSL
 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to RGB
 */
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b]
    .map(x => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');
}

/**
 * Darken a hex color by a percentage
 * @param hex - Hex color string (e.g., '#0f3a60')
 * @param percent - Percentage to darken (0-100)
 * @returns Darkened hex color
 */
export function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Decrease lightness
  hsl.l = Math.max(0, hsl.l - percent);

  const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}

/**
 * Lighten a hex color by a percentage
 * @param hex - Hex color string (e.g., '#0f3a60')
 * @param percent - Percentage to lighten (0-100)
 * @returns Lightened hex color
 */
export function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Increase lightness
  hsl.l = Math.min(100, hsl.l + percent);

  const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}

/**
 * Parse a hex colour string into RGB. Handles `#rgb` and `#rrggbb` (with or
 * without the leading `#`) and returns null for anything it can't parse, so
 * callers can fall back deliberately. (The internal `hexToRgb` above assumes
 * 6-digit input and silently yields black for other forms.)
 */
function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  if (typeof hex !== 'string') return null;
  let clean = hex.trim().replace(/^#/, '');
  if (clean.length === 3) {
    clean = clean.replace(/./g, (c) => c + c);
  }
  if (clean.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  const value = parseInt(clean, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

/**
 * WCAG relative luminance (0 = black, 1 = white) for an sRGB colour.
 */
function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

// Dark foreground used on pale themed backgrounds — matches the card's existing
// dark text (`#1f2937`). Luminance derived from the colour itself so the two
// can't drift.
const ON_PRIMARY_DARK = '#1f2937';
const ON_PRIMARY_LIGHT = '#ffffff';
const ON_PRIMARY_DARK_LUM = relativeLuminance(parseHexColor(ON_PRIMARY_DARK)!);

// WCAG AA contrast for normal text. Used when deriving a primary colour that
// must stay legible as a foreground on a white surface.
const READABLE_MIN_CONTRAST = 4.5;

/** Contrast ratio of a colour against white (the lightest surface accents sit on). */
function contrastWithWhite(rgb: { r: number; g: number; b: number }): number {
  return (1 + 0.05) / (relativeLuminance(rgb) + 0.05);
}

/**
 * Normalise a hex colour string to canonical `#rrggbb`, or null if it isn't a
 * parseable hex value. Lets callers validate an org's stored colour before it
 * reaches CSS.
 */
export function normalizeHexColor(hex: string): string | null {
  const rgb = parseHexColor(hex);
  return rgb ? rgbToHex(rgb.r, rgb.g, rgb.b) : null;
}

/** `"r, g, b"` triplet for a hex colour (for `rgba(var(--x), a)`), or null. */
export function hexToRgbString(hex: string): string | null {
  const rgb = parseHexColor(hex);
  return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : null;
}

// A brand colour lighter than this (silver, gold, pastels, white) washes white text out; everything darker
// wears white, as a brand does. Picking the shade with the most contrast instead put near-black on
// saturated mid-tones (Atlantic's #16af00: 5:1 dark, 2.9:1 white), which the dealer did not want.
const PALE_LUMINANCE = 0.5;

/**
 * Foreground colour (white or near-black) for text and icons painted on `hex`. Org branding lets
 * customers choose any primary colour, and a white or pale one would make hard-coded white button
 * text invisible, so the text colour is derived from the actual background.
 *
 * @param hex - Background hex colour (e.g. the org's primary colour)
 * @returns `#ffffff` unless the colour is pale, then `#1f2937`. Falls back to white for
 *          unparseable input (matches the prior hard-coded value).
 */
export function getContrastColor(hex: string): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return ON_PRIMARY_LIGHT;
  return relativeLuminance(rgb) > PALE_LUMINANCE ? ON_PRIMARY_DARK : ON_PRIMARY_LIGHT;
}

// Primary buttons dim to this on hover (app.scss .btn-primary: filter: brightness(0.86)); keep in sync.
const HOVER_BRIGHTNESS = 0.86;
// WCAG's floor for UI components and large text; good enough for a hover state.
const HOVER_FLIP_MIN_RATIO = 3;

/**
 * Text colour for a primary button on hover: the opposite shade of the resting
 * `getContrastColor` pick, so the hover reads as a change, but only when that
 * shade still clears 3:1 against the (slightly dimmed) hover background.
 * Bright brand colours flip white → dark; dark ones stay white, since dark
 * text on navy would vanish; pale ones stay dark, since white on yellow would.
 */
export function getHoverContrastColor(hex: string): string {
  const rest = getContrastColor(hex);
  const rgb = parseHexColor(hex);
  if (!rgb) return rest;
  const hoverLum = relativeLuminance({ r: rgb.r * HOVER_BRIGHTNESS, g: rgb.g * HOVER_BRIGHTNESS, b: rgb.b * HOVER_BRIGHTNESS });
  const flipped = rest === ON_PRIMARY_DARK ? ON_PRIMARY_LIGHT : ON_PRIMARY_DARK;
  const ratio = flipped === ON_PRIMARY_LIGHT
    ? (1 + 0.05) / (hoverLum + 0.05)
    : (hoverLum + 0.05) / (ON_PRIMARY_DARK_LUM + 0.05);
  return ratio >= HOVER_FLIP_MIN_RATIO ? flipped : rest;
}

/**
 * Return a version of the org primary colour that stays legible as a
 * text/icon/border accent on a light (white) surface. The primary is used not
 * only as a button background but also as a foreground on white cards (CTAs,
 * labels, focus rings) — a white or pale brand colour would be invisible there.
 *
 * If the colour already meets WCAG AA against white it's returned unchanged, so
 * normal/dark brand colours look exactly as before; otherwise it's darkened —
 * hue preserved — until it does.
 *
 * @param hex - The org's primary colour
 * @returns A legible hex colour. Falls back to `#1f2937` for unparseable input.
 */
export function getReadableOnLight(hex: string): string {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return ON_PRIMARY_DARK;

  if (contrastWithWhite(parseHexColor(normalized)!) >= READABLE_MIN_CONTRAST) {
    return normalized;
  }

  // Darken in steps, preserving hue. Darkening always reaches black (21:1 on
  // white), so this terminates.
  for (let amount = 5; amount <= 100; amount += 5) {
    const candidate = darkenColor(normalized, amount);
    const rgb = parseHexColor(candidate);
    if (rgb && contrastWithWhite(rgb) >= READABLE_MIN_CONTRAST) return candidate;
  }
  return ON_PRIMARY_DARK;
}
